import type { ReactNode } from 'react';
import { Eraser, Info, Undo2 } from 'lucide-react';
import { OVERLAY_ATTRIBUTE } from '../board-grid/useZoomPan';

interface PhotoOverlayToolsProps {
  undoLabel: string;
  onUndo: () => void;
  canUndo: boolean;
  clearLabel: string;
  onClear: () => void;
  canClear: boolean;
  helpLabel: string;
  onHelp: () => void;
}

/**
 * Taking the drawing back and asking what any of it means, in the corner of the
 * picture it is about.
 *
 * Not on the panel that stands at the mark in hand: that panel comes and goes
 * with what is in hand, and undo has to be reachable precisely when nothing is —
 * after the wall has been wiped, most of all. The (i) is here for the other half
 * of that reason: somebody who does not yet know what the colours mean has not
 * drawn anything to open a panel with.
 *
 * Top-right of the picture, which is the corner both surfaces keep for it: the
 * zoom's way out is bottom-left, and changing the photo is top-left.
 */
export function PhotoOverlayTools({
  undoLabel,
  onUndo,
  canUndo,
  clearLabel,
  onClear,
  canClear,
  helpLabel,
  onHelp,
}: PhotoOverlayToolsProps) {
  return (
    <div
      className="absolute right-2 top-2 flex items-center gap-1"
      {...{ [OVERLAY_ATTRIBUTE]: '' }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <PhotoOverlayButton label={undoLabel} onClick={onUndo} disabled={!canUndo}>
        <Undo2 className="size-4" aria-hidden />
      </PhotoOverlayButton>
      <PhotoOverlayButton label={clearLabel} onClick={onClear} disabled={!canClear}>
        <Eraser className="size-4" aria-hidden />
      </PhotoOverlayButton>
      <PhotoOverlayButton label={helpLabel} onClick={onHelp}>
        <Info className="size-4" aria-hidden />
      </PhotoOverlayButton>
    </div>
  );
}

/**
 * A control that floats on the picture — dark, round and translucent, so it
 * reads over a photo of any wall without a panel behind it.
 *
 * `pressed` makes it a toggle rather than an action: it then says so to a screen
 * reader and wears it, filled in white, which is the one thing that stands out
 * from the dark buttons beside it on any photograph.
 */
export function PhotoOverlayButton({
  label,
  onClick,
  disabled,
  pressed,
  title,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  /** Spelled out on hover, where the icon alone does not say enough. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      aria-label={label}
      title={title ?? label}
      className={`flex size-9 items-center justify-center rounded-full shadow-lg backdrop-blur transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        pressed ? 'bg-white text-black hover:bg-white/90' : 'bg-black/60 text-white hover:bg-black/75'
      }`}
    >
      {children}
    </button>
  );
}
